import { Position, type InternalNode, type Node } from "@xyflow/react";

import {
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
} from "./workspaceGraphConstants";

type NodeBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function readNodeBox(node: InternalNode<Node>): NodeBox {
  const absolute =
    node.internals?.positionAbsolute ??
    node.position ??
    ({ x: 0, y: 0 } as { x: number; y: number });
  const width =
    (typeof node.measured?.width === "number" && node.measured.width > 0
      ? node.measured.width
      : typeof node.width === "number" && node.width > 0
        ? node.width
        : WORKSPACE_NODE_CARD_WIDTH) || WORKSPACE_NODE_CARD_WIDTH;
  const height =
    (typeof node.measured?.height === "number" && node.measured.height > 0
      ? node.measured.height
      : typeof node.height === "number" && node.height > 0
        ? node.height
        : WORKSPACE_NODE_CARD_HEIGHT) || WORKSPACE_NODE_CARD_HEIGHT;
  return { x: absolute.x, y: absolute.y, width, height };
}

/**
 * Intersection of the center→center ray with `intersectionNode`'s border.
 * Classic React Flow floating-edge math (rectangular cards).
 */
export function getWorkspaceNodeIntersection(
  intersectionNode: InternalNode<Node>,
  targetNode: InternalNode<Node>
): { x: number; y: number } {
  const a = readNodeBox(intersectionNode);
  const b = readNodeBox(targetNode);
  const w = a.width / 2;
  const h = a.height / 2;
  const x2 = a.x + w;
  const y2 = a.y + h;
  const x1 = b.x + b.width / 2;
  const y1 = b.y + b.height / 2;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const denom = Math.abs(xx1) + Math.abs(yy1);
  const aScale = denom > 0 ? 1 / denom : 0;
  const xx3 = aScale * xx1;
  const yy3 = aScale * yy1;
  return {
    x: w * (xx3 + yy3) + x2,
    y: h * (-xx3 + yy3) + y2,
  };
}

function getWorkspaceEdgePosition(
  node: InternalNode<Node>,
  point: { x: number; y: number }
): Position {
  const box = readNodeBox(node);
  const px = point.x;
  const py = point.y;
  const eps = 1;
  if (Math.abs(px - box.x) <= eps) return Position.Left;
  if (Math.abs(px - (box.x + box.width)) <= eps) return Position.Right;
  if (Math.abs(py - box.y) <= eps) return Position.Top;
  if (Math.abs(py - (box.y + box.height)) <= eps) return Position.Bottom;
  // Fallback: nearest side by distance.
  const distLeft = Math.abs(px - box.x);
  const distRight = Math.abs(px - (box.x + box.width));
  const distTop = Math.abs(py - box.y);
  const distBottom = Math.abs(py - (box.y + box.height));
  const min = Math.min(distLeft, distRight, distTop, distBottom);
  if (min === distLeft) return Position.Left;
  if (min === distRight) return Position.Right;
  if (min === distTop) return Position.Top;
  return Position.Bottom;
}

export type WorkspaceFloatingEdgeParams = {
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  sourcePos: Position;
  targetPos: Position;
};

/** Border attachment points aimed at each card's center. */
export function getWorkspaceFloatingEdgeParams(
  source: InternalNode<Node>,
  target: InternalNode<Node>
): WorkspaceFloatingEdgeParams {
  const sourceIntersection = getWorkspaceNodeIntersection(source, target);
  const targetIntersection = getWorkspaceNodeIntersection(target, source);
  return {
    sx: sourceIntersection.x,
    sy: sourceIntersection.y,
    tx: targetIntersection.x,
    ty: targetIntersection.y,
    sourcePos: getWorkspaceEdgePosition(source, sourceIntersection),
    targetPos: getWorkspaceEdgePosition(target, targetIntersection),
  };
}
